// This file is effectively replacing the selectors folder
// It will allow us to use a combination of react-query and redux
import {
  type CompanyType,
  GetApiClustersForTimesheetResponse,
  type GetApiDimensionsQueryParamsUpdated,
  GetApiTimesheetsByUuidEntriesResponse,
  isTokenExpired,
  TimesheetType,
  useGetTimesheet,
} from '@sit/client-shared';
import { useGetCurrentCompany } from '@web/api/company/use-get-company';
import { useGetDimensions } from '@web/api/dimensions/use-get-dimensions';
import { useShowIntacctId } from '@web/api/show-intacct-id';
import { useGetTimesheetEntries } from '@web/api/timesheet-entry/use-get-timesheet-entries';
import { useGetUser } from '@web/api/users/use-get-user';
import { internalTenantTypeRegEx } from '@web/constants/regularExpressions';
import { includeIntacctId } from '@web/helpers/utils';
import compact from 'lodash/compact';
import { useCallback, useMemo } from 'react';
import type { SharedUnionFieldsDeep } from 'type-fest';
import { useToken } from '../auth-store';
import { selectIsPendingResponse } from '../selectors';
import {
  filterAssignedClusters,
  filterUnassignedClusters,
  groupAndSortClusters,
  includeIntacctIdInClusterDimensionNames,
  includeIntacctIdInEntryDimensionNames,
} from '../selectors/helpers';
import { useAppSelector } from '../store';

// company
export function useCompanyEntities() {
  const { data: company } = useGetCurrentCompany();
  return company?.entities;
}

export function useIsInternalTenant() {
  const { data: company } = useGetCurrentCompany();
  return internalTenantTypeRegEx.test(company?.tenantType || 'debug');
}

// timesheets

function selectIncludeIntacctId(data: GetApiTimesheetsByUuidEntriesResponse) {
  return data.map((entry) => includeIntacctIdInEntryDimensionNames(entry));
}

export function useTimesheetEntriesData(timesheetId: string) {
  const showIntacctId = useShowIntacctId();
  const select = useCallback(
    (data: GetApiTimesheetsByUuidEntriesResponse) => (showIntacctId ? selectIncludeIntacctId(data) : data),
    [showIntacctId],
  );
  return useGetTimesheetEntries(
    {
      pathParams: {
        id: timesheetId,
      },
    },
    { select },
  );
}

// Timesheet
export function useTimesheetSyncStatus(timesheetId: string) {
  return useAppSelector((state) => state.timesheets[timesheetId]?.synced) ?? false;
}

export function useTimesheetDescription(id: string | undefined) {
  return useGetTimesheet(id).data?.description;
}
export function useTimesheetStatus(id: string) {
  return useGetTimesheet(id).data?.status || 'NO_STATUS';
}
export function useDeleteRowDisabled() {
  // Timesheet delete calls Intacct directly synchronously. This operation can take a long time, especially on a large declined timesheet.
  // We want to disable the option to delete another row while this operation is in progress.
  return useAppSelector((state) => selectIsPendingResponse(state, 'DELETE_ROW'));
}

export function useSubmitButtonDisabled(timesheetId: string) {
  const timesheetStatus = useTimesheetStatus(timesheetId);
  const { data: entriesData } = useGetTimesheetEntries({
    pathParams: {
      id: timesheetId,
    },
  });
  // Timesheet delete calls Intacct directly synchronously. This operation can take a long time, especially on a large declined timesheet.
  // We want to disable the delete button while this operation is in progress.
  const isPerformingOperation = useDeleteRowDisabled();
  return isPerformingOperation || /^(INVOICED)$/.test(timesheetStatus) || !entriesData || Object.keys(entriesData).length === 0;
}

// Dimensions

export function useDimensionsData(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const { data: dimensionsData = [] } = useGetDimensions(queryParams);
  const showIntacctId = useShowIntacctId();
  return useMemo(() => {
    if (!showIntacctId) return dimensionsData;
    return dimensionsData.map((d) => ({
      ...d,
      values: d.values?.map((v) => includeIntacctId(v)),
    }));
  }, [dimensionsData, showIntacctId]);
}

export function useClusterEntry(timesheetId: string, cluster: { timesheet_entry_id?: string | null | undefined } | undefined) {
  const showIntacctId = useShowIntacctId();
  const { data: entriesData } = useGetTimesheetEntries({
    pathParams: {
      id: timesheetId,
    },
  });
  const clusterEntryId = cluster?.timesheet_entry_id;
  if (!clusterEntryId || !entriesData) return null;
  const entry = entriesData.find((e) => e.id === clusterEntryId);
  if (!showIntacctId || !entry) return entry || null;
  return {
    ...entry,
    client: entry.client ? includeIntacctId(entry.client) : undefined,
    project: entry.project ? includeIntacctId(entry.project) : undefined,
    task: entry.task ? includeIntacctId(entry.task) : undefined,
    item: entry.item ? includeIntacctId(entry.item) : undefined,
  };
}

export function useAssignedClusters(clusters: GetApiClustersForTimesheetResponse, timesheetId: string) {
  const showIntacctId = useShowIntacctId();
  const { data: entriesData } = useGetTimesheetEntries({
    pathParams: {
      id: timesheetId,
    },
  });
  return useMemo(() => {
    if (!showIntacctId) return groupAndSortClusters(filterAssignedClusters(clusters, entriesData, timesheetId));
    const updatedClusterData: GetApiClustersForTimesheetResponse = compact(clusters.map((c) => includeIntacctIdInClusterDimensionNames(c)));
    return groupAndSortClusters(filterAssignedClusters(updatedClusterData, entriesData, timesheetId));
  }, [clusters, entriesData, showIntacctId, timesheetId]);
}

export function useUnassignedClusters(clusters: GetApiClustersForTimesheetResponse) {
  const showIntacctId = useShowIntacctId();
  return useMemo(() => {
    if (!showIntacctId) return groupAndSortClusters(filterUnassignedClusters(clusters));
    const updatedClusterData = clusters.map((c) => includeIntacctIdInClusterDimensionNames(c));
    return groupAndSortClusters(filterUnassignedClusters(updatedClusterData));
  }, [clusters, showIntacctId]);
}

function useShowDim<D extends keyof SharedUnionFieldsDeep<CompanyType | TimesheetType>>(timesheetId: string | undefined, dim: D) {
  const { data: timesheetData } = useGetTimesheet(timesheetId);
  const { data: company } = useGetCurrentCompany();
  return timesheetData?.[dim] ?? !!company?.[dim];
}

/**
 * @param timesheetId `undefined` must only used when creating demo activity cards
 */
export function useShowDepartmentLocation(timesheetId: string | undefined) {
  return useShowDim(timesheetId, 'showDepartmentLocation');
}

/**
 * @param timesheetId `undefined` must only used when creating demo activity cards
 */
export function useShowDescriptionFirst(timesheetId: string | undefined) {
  return useShowDim(timesheetId, 'showDescriptionFirst');
}

// Companies
export function useAttachmentRights() {
  const { data: company } = useGetCurrentCompany();
  const { data: user } = useGetUser();
  return {
    canEditAttachments: !!company?.canEditAttachments && !!user?.canEditAttachments,
    canDownloadAttachments: !!company?.canDownloadAttachments && !!user?.canDownloadAttachments,
    canDeleteAttachments: !!company?.canDeleteAttachments && !!user?.canDeleteAttachments,
    canListAttachments: !!company?.canListAttachments && !!user?.canListAttachments,
  };
}
// auth
export function useIsAuthenticated() {
  // Grace period for token expiration in milliseconds
  const requestGracePeriod = 1000;

  const storybookAuthByPass = import.meta.env.MODE === 'development' && import.meta.env.STORYBOOK === 'true';
  const token = useToken();
  return storybookAuthByPass || (token != null && !isTokenExpired(token, requestGracePeriod));
}
